<template>
  <div>
    <v-alert
      v-if="!hasStatistics"
      type="info"
    >
      {{ $t('This page will contain statistics about your FAQ once we have some data to present') }}
    </v-alert>
    <div>
      <v-card
        v-if="$vuetify.breakpoint.mdAndUp"
        class="mb-4"
      >
        <v-card-text>
          <v-btn
            color="primary"
            :href="route('faq.stats.export-csv', faq.id)"
          >
            {{ $t('Export CSV') }}
          </v-btn>

          <div class="text-center mb-2">
            <v-btn-toggle
              v-model="activeGraph"
              tile
              color="primary"
              dense
              group
              mandatory
            >
              <v-btn value="last-30">
                {{ $t('Last 30 days') }}
              </v-btn>

              <v-btn
                v-if="statsMonthlyChart.labels.length > 2"
                value="monthly"
              >
                {{ $t('Monthly') }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <stats-last-thirty-days-chart
            v-if="activeGraph === 'last-30'"
            :height="120"
            :labels="statsLastThirtyDaysChart.labels"
            :datasets="statsLastThirtyDaysChart.datasets"
            :views-color="colors.views"
            :interactions-color="colors.interactions"
            :avg-interactions-color="colors.avgInteractions"
            :display-title="false"
          />

          <stats-monthly-chart
            v-if="statsMonthlyChart.labels.length > 2 && activeGraph === 'monthly'"
            class="mt-4"
            :height="120"
            :labels="statsMonthlyChart.labels"
            :datasets="statsMonthlyChart.datasets"
            :views-color="colors.views"
            :interactions-color="colors.interactions"
            :avg-interactions-color="colors.avgInteractions"
            :display-title="false"
          />
        </v-card-text>
      </v-card>
      <v-row>
        <v-col
          cols="12"
          sm="6"
        >
          <stats-card
            :color="colors.views"
            :title="$t('Total views')"
            :content="hasStatistics ? totalViews.toLocaleString() : '-'"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <stats-card
            :color="colors.interactions"
            :title="$t('Total interactions')"
            :content="hasStatistics ? totalInteractions.toLocaleString() : '-'"
          />
        </v-col>
        <!-- <v-col
          cols="12"
          sm="4"
        >
          <stats-card
            :color="colors.avgInteractions"
            :title="$t('Average interactions per session')"
            :content="hasStatistics ? avgInteractionsPerSession.toFixed(2) : '-'"
          />
        </v-col> -->
      </v-row>

      <v-row v-if="faq.embed_user_feedback">
        <v-col
          cols="12"
          sm="6"
        >
          <stats-card-table
            :title="$t('Most liked questions/answers')"
            :color="colors.positive"
          >
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('Question') }}
                </th>
                <th class="text-left">
                  {{ $t('Upvotes') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="mostLikedQAs.length === 0">
                <td>-</td>
                <td>-</td>
              </tr>
              <tr
                v-for="(obj, idx) in mostLikedQAs"
                :key="`question-${obj.id}-${idx}`"
              >
                <td>
                  <inertia-link :href="route('faq.question.edit', [faq.id, obj.id])">
                    {{ obj.title }}
                  </inertia-link>
                </td>
                <td>{{ obj.upvotes_count }}</td>
              </tr>
            </tbody>
          </stats-card-table>
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <stats-card-table
            :title="$t('Least liked questions/answers')"
            :color="colors.negative"
          >
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('Question') }}
                </th>
                <th class="text-left">
                  {{ $t('Downvotes') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="leastLikedQAs.length === 0">
                <td>-</td>
                <td>-</td>
              </tr>
              <tr
                v-for="(obj, idx) in leastLikedQAs"
                :key="`question-${obj.id}-${idx}`"
              >
                <td>
                  <inertia-link :href="route('faq.question.edit', [faq.id, obj.id])">
                    {{ obj.title }}
                  </inertia-link>
                </td>
                <td>{{ obj.downvotes_count }}</td>
              </tr>
            </tbody>
          </stats-card-table>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          sm="6"
        >
          <stats-card-table
            :title="$t('Most popular questions')"
            :color="colors.positive"
          >
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('Question') }}
                </th>
                <th class="text-left">
                  {{ $t('Views') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="mostPopularQuestions.length === 0">
                <td>-</td>
                <td>-</td>
              </tr>
              <tr
                v-for="(obj, idx) in mostPopularQuestions"
                :key="`question-${obj.id}-${idx}`"
              >
                <td>
                  <inertia-link :href="route('faq.question.edit', [faq.id, obj.id])">
                    {{ obj.title }}
                  </inertia-link>
                </td>
                <td>{{ obj.views_count }}</td>
              </tr>
            </tbody>
          </stats-card-table>
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <stats-card-table
            :title="$t('Least popular questions')"
            :color="colors.negative"
          >
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('Question') }}
                </th>
                <th class="text-left">
                  {{ $t('Views') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="leastPopularQuestions.length === 0">
                <td>-</td>
                <td>-</td>
              </tr>
              <tr
                v-for="(obj, idx) in leastPopularQuestions"
                :key="`question-${obj.id}-${idx}`"
              >
                <td>
                  <inertia-link :href="route('faq.question.edit', [faq.id, obj.id])">
                    {{ obj.title }}
                  </inertia-link>
                </td>
                <td>{{ obj.views_count }}</td>
              </tr>
            </tbody>
          </stats-card-table>
        </v-col>
      </v-row>

      <v-row v-if="faq.categories.length > 1">
        <v-col
          cols="12"
          sm="6"
        >
          <stats-card-table
            :title="$t('Most popular categories')"
            :color="colors.positive"
          >
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('Category') }}
                </th>
                <th class="text-left">
                  {{ $t('Views') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="mostPopularCategories.length === 0">
                <td>-</td>
                <td>-</td>
              </tr>
              <tr
                v-for="(obj, idx) in mostPopularCategories"
                :key="`category-${obj.id}-${idx}`"
              >
                <td>{{ obj.name }}</td>
                <td>{{ obj.views_count }}</td>
              </tr>
            </tbody>
          </stats-card-table>
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <stats-card-table
            :title="$t('Least popular categories')"
            :color="colors.negative"
          >
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('Category') }}
                </th>
                <th class="text-left">
                  {{ $t('Views') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="leastPopularCategories.length === 0">
                <td>-</td>
                <td>-</td>
              </tr>
              <tr
                v-for="(obj, idx) in leastPopularCategories"
                :key="`category-${obj.id}-${idx}`"
              >
                <td>{{ obj.name }}</td>
                <td>{{ obj.views_count }}</td>
              </tr>
            </tbody>
          </stats-card-table>
        </v-col>
      </v-row>

      <v-row v-if="viewsPerDomain.length > 0">
        <v-col
          cols="12"
          sm="6"
        >
          <stats-card-table :title="$t('Views per domain')">
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('Domain') }}
                </th>
                <th class="text-left">
                  {{ $t('Views') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="viewsPerDomain.length === 0">
                <td>-</td>
                <td>-</td>
              </tr>
              <tr
                v-for="(obj, idx) in viewsPerDomain"
                :key="`domain-${obj.domain}-${idx}`"
              >
                <td>{{ obj.domain }}</td>
                <td>{{ obj.views_count }}</td>
              </tr>
            </tbody>
          </stats-card-table>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          sm="4"
        >
          <v-card>
            <v-card-text class="pb-0">
              <p class="mb-0">
                {{ $t('Latest searches') }}
              </p>
            </v-card-text>
            <v-list>
              <v-list-item v-if="latestSearches.length === 0">
                <v-list-item-content>
                  <v-list-item-title>-</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-for="(obj, idx) in latestSearches"
                :key="`search-${obj.search_query}-${idx}`"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ obj.search_query }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          sm="4"
        >
          <stats-card-table :title="$t('Most popular searches')">
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('Query') }}
                </th>
                <th class="text-left">
                  {{ $t('Count') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="mostPopularSearches.length === 0">
                <td>-</td>
                <td>-</td>
              </tr>
              <tr
                v-for="(obj, idx) in mostPopularSearches"
                :key="`popular-search-${obj.search_query}-${idx}`"
              >
                <td>{{ obj.search_query }}</td>
                <td>{{ obj.search_count }}</td>
              </tr>
            </tbody>
          </stats-card-table>
        </v-col>
        <v-col
          cols="12"
          sm="4"
        >
          <stats-card-table :title="$t('Most popular searches with no results')">
            <thead>
              <tr>
                <th class="text-left">
                  {{ $t('Query') }}
                </th>
                <th class="text-left">
                  {{ $t('Count') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="mostPopularSearchesWithoutResults.length === 0">
                <td>-</td>
                <td>-</td>
              </tr>
              <tr
                v-for="(obj, idx) in mostPopularSearchesWithoutResults"
                :key="`popular-search-${obj.search_query}-${idx}`"
              >
                <td>{{ obj.search_query }}</td>
                <td>{{ obj.search_count }}</td>
              </tr>
            </tbody>
          </stats-card-table>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import omit from 'lodash/omit';
import some from 'lodash/some';
import isNumber from 'lodash/isNumber';
import isArray from 'lodash/isArray';
import StatsLastThirtyDays from '@/components/charts/StatsLastThirtyDays';
import StatsMonthly from '@/components/charts/StatsMonthly';
import Layout from '@/components/shared/TheLayout';
import TabsLayout from '@/components/faq/TabsLayout';
import colors from 'vuetify/lib/util/colors';

export default {
  components: {
    StatsLastThirtyDaysChart: StatsLastThirtyDays,
    StatsMonthlyChart: StatsMonthly,
  },

  layout: (h, page) => h(Layout, {
    props: {
      noWrappingContainer: true,
      pageTitle: page.data.props.faq.default_translation.name
    }
  }, [h(TabsLayout, {
    props: {
      activeTab: 'overview',
      faq: page.data.props.faq,
      forceHidePreview: true
    }
  }, [page])]),

  props: {
    faq: {
      type: Object,
      required: true
    },
    totalViews: {
      type: Number,
      default: 0
    },
    totalInteractions: {
      type: Number,
      default: 0
    },
    mostLikedQAs: {
      type: Array,
      required: false,
      default: () => []
    },
    leastLikedQAs: {
      type: Array,
      required: false,
      default: () => []
    },
    avgInteractionsPerSession: {
      type: Number,
      default: 0
    },
    mostPopularQuestions: {
      type: Array,
      required: true,
      default: () => []
    },
    leastPopularQuestions: {
      type: Array,
      required: true,
      default: () => []
    },
    mostPopularCategories: {
      type: Array,
      required: true,
      default: () => []
    },
    leastPopularCategories: {
      type: Array,
      required: true,
      default: () => []
    },
    viewsPerDomain: {
      type: [Object, Array],
      required: true,
      default: () => {}
    },
    latestSearches: {
      type: Array,
      required: true,
      default: () => []
    },
    mostPopularSearches: {
      type: Array,
      required: true,
      default: () => []
    },
    mostPopularSearchesWithoutResults: {
      type: Array,
      required: true,
      default: () => []
    },
    statsLastThirtyDaysChart: {
      type: Object,
      required: true
    },
    statsMonthlyChart: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      search: '',
      activeGraph: 'last-30',
      colors: {
        views: colors.teal.base,
        interactions: colors.purple.base,
        avgInteractions: colors.blue.base,
        positive: colors.green.base,
        negative: colors.red.base
      }
    }
  },

  computed: {
    hasStatistics() {
      const skipProps = ['faq'];
      const props = omit(this.$props, skipProps);

      return some(props, (value, key) => {
        if (isArray(value)) {
          return value.length > 0;
        } else if (isNumber(value)) {
          return value > 0;
        }
      });
    }
  },
}
</script>